import React from "react";
import { withProps } from "recompose";
import { Provider } from "react-redux";

import { InfoWindowV2 } from "components/core/GoogleMap";
import FacilityCardV2 from "components/organisms/search/FacilityCards/FacilityCardV2";

const getAddress = (facility, location) => {
	if (facility?.address) return facility.address;

	const streetAddress1 = location?.streetAddress1 || "";
	const city = location?.city || "";
	const state = location?.state || "";
	const zip = location?.zip || "";

	return [
		streetAddress1,
		[city, state].filter(Boolean).join(", "),
		zip
	]
		.filter(Boolean)
		.join(" ")
		.trim();
};

const getSchema = (facility, location) => {
	const schemaLocation = facility?.schema?.location || {};
	const mergedLocation = {
		...schemaLocation,
		...location,
		city: schemaLocation.city || location?.city,
		state: schemaLocation.state || location?.state,
		latitude:
			schemaLocation.latitude != null
				? schemaLocation.latitude
				: location?.latitude,
		longitude:
			schemaLocation.longitude != null
				? schemaLocation.longitude
				: location?.longitude
	};

	return {
		...(facility?.schema || {}),
		location: mergedLocation,
		url: facility?.schema?.url || facility?.url,
		name: facility?.schema?.name || facility?.name,
		reviews: facility?.schema?.reviews || facility?.reviews || {
			average: 0,
			number: 0
		}
	};
};

const getOnlineMoveinsFlag = (facility) => {
	const rawValue =
		facility?.hasOnlineMoveins ??
		facility?.hasOnlineMoveIns ??
		facility?.schema?.hasOnlineMoveins ??
		facility?.schema?.hasOnlineMoveIns ??
		facility?.schema?.amenities?.hasOnlineMoveins ??
		facility?.schema?.amenities?.hasOnlineMoveIns;

	if (typeof rawValue === "string") {
		return rawValue.toLowerCase() === "true";
	}

	return Boolean(rawValue);
};

const mapFacilityToCardProps = (facility) => {
	const location = facility?.location || facility?.schema?.location || {};
	const imageFromSchema = Array.isArray(facility?.schema?.images)
		? facility.schema.images[0]
		: undefined;

	return {
		name: facility?.name,
		layout: facility?.layout || "narrow",
		image: facility?.image || imageFromSchema,
		showDefaultImage: Boolean(facility?.showDefaultImage),
		address: getAddress(facility, location),
		distance:
			typeof facility?.distance === "number"
				? facility.distance
				: 0,
		url: facility?.url || facility?.schema?.url,
		lowestPrice:
			typeof facility?.lowestPrice === "number"
				? facility.lowestPrice
				: facility?.startingPrice,
		startingPrice:
			typeof facility?.startingPrice === "number"
				? facility.startingPrice
				: facility?.lowestPrice,
		reviews: facility?.reviews || {
			average: 0,
			number: 0
		},
		schema: getSchema(facility, location),
		totalFees: facility?.totalFees,
		lockFee: facility?.lockFee,
		adminFee: facility?.adminFee,
		showFeeWarning: Boolean(facility?.showFeeWarning),
		hasOnlineMoveins: getOnlineMoveinsFlag(facility)
	};
};

export const enhance = withProps(
	({
		actions,
		facility,
		handleFacilityCardVisible,
		handleFacilitySelected,
		handleFacilityDeselected,
		handlePinDeselected,
		searchType,
		searchParams,
		inMapView,
		reduxStore
	}) => {
		const cardProps = mapFacilityToCardProps(facility);
		const locationData = cardProps.schema?.location || {};

		const cardContent = (
			<FacilityCardV2
				{...cardProps}
				getFacilityCardPhoneNumber={() =>
					actions?.getFacilityCardPhoneNumber
						? actions.getFacilityCardPhoneNumber(
								locationData?.city,
								locationData?.state,
								facility?.id
						  )
						: Promise.resolve({})
				}
				handleFacilitySelected={handleFacilitySelected}
				handleFacilityDeselected={handleFacilityDeselected}
				handlePinDeselected={handlePinDeselected}
				miniCard
				lazyLoad={false}
				onVisible={handleFacilityCardVisible}
				storageType={searchType}
				searchParams={searchParams}
				inMapView={inMapView}
			/>
		);

		return {
			location: facility?.location || facility?.schema?.location,
			content: reduxStore ? (
				<Provider store={reduxStore}>{cardContent}</Provider>
			) : (
				cardContent
			)
		};
	}
);

export default enhance(InfoWindowV2);
